import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, Stack, Tooltip, Typography, styled } from '@mui/material';
import { DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import { useAtom, useAtomValue } from 'jotai';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isDateInputValid } from '../../../../../components/DatePicker/Calendar';
import { CalendarCurrency } from '../../../../../components/DatePicker/CalendarCurrency';
import { Breakpoint, ContainerName } from '../../../../../constants/styles';
import { ViewTypeByData } from '../../../../../data-models/view-config.data-model';
import { useSelectedCurrency } from '../../../../../hooks/useSelectedCurrency';
import {
  dateToPlainDateString,
  isValidDate,
  nextQuarterEnd,
  previousQuarterEnd,
} from '../../../../../util/date-utilts';
import { selectedDateByViewPFJ } from '../../../state/PageStateJ';
import { selectedViewIdPFJ, selectedViewPFJ } from '../../../state/ViewStateJ';
import { portfolioDateShortcuts } from './portfolioDateShortcuts';

/** With the popup shut there is no dismissal to commit on, so a typed date applies on its own. */
const TYPED_DATE_APPLY_MS = 1500;
/** Chevrons, rail chips and day clicks apply once the clicks stop, so a burst of them is one reload. */
export const DATE_STEP_APPLY_MS = 250;

const StyledLabel = styled(Typography)`
  padding-right: 0.5rem;
  font-size: 0.825rem;
  text-align: right;
  @container ${ContainerName.headerContainer} (max-width: ${Breakpoint.hideBreadcrumbs}) {
    display: none;
  }
`;

const viewsWithDatePicker = new Set<ViewTypeByData>([
  ViewTypeByData.fund,
  ViewTypeByData.company,
  ViewTypeByData.transaction,
]);

interface IPortfolioDatePickerProps {
  /** The page is loading the applied date; every control waits for it. */
  isLoading?: boolean;
}

export function PortfolioDatePicker({ isLoading = false }: IPortfolioDatePickerProps) {
  const viewId = useAtomValue(selectedViewIdPFJ);
  const selectedView = useAtomValue(selectedViewPFJ);
  const [viewDate, setViewDate] = useAtom(selectedDateByViewPFJ(viewId));
  const { selectedCurrency } = useSelectedCurrency();

  // undefined = nothing uncommitted; null = the user cleared the field.
  const [pending, setPending] = useState<Date | null | undefined>(undefined);
  // Mirrors `pending` for a handler that runs in the event that set it, before the re-render.
  const pendingRef = useRef<Date | null | undefined>(undefined);
  // A ref, not state: the open flag is read in the same event that may have just set it.
  const popupOpenRef = useRef(false);

  const shown = pending === undefined ? viewDate : pending;
  const stepFrom = shown && isValidDate(shown) ? shown : viewDate;

  const shortcutItems = useMemo(() => portfolioDateShortcuts(stepFrom), [stepFrom]);

  const showPending = useCallback((next: Date | null | undefined) => {
    pendingRef.current = next;
    setPending(next);
  }, []);

  const applyDate = useCallback(
    (next: Date | null) => {
      // Cleared first, so a date that cannot be applied leaves the field on the one that is.
      showPending(undefined);
      // Future as-of dates are valid (views are forward-dated). Never bound this or the picker by today.
      if (!next || !isDateInputValid(next)) {
        return;
      }
      // Everything downstream reloads per distinct day, so a move within one day must not write.
      setViewDate((prev) => (dateToPlainDateString(prev) === dateToPlainDateString(next) ? prev : next));
    },
    [setViewDate, showPending]
  );

  const commitTyped = useMemo(() => debounce(applyDate, TYPED_DATE_APPLY_MS), [applyDate]);
  useEffect(() => () => commitTyped.cancel(), [commitTyped]);

  const commitSoon = useMemo(() => debounce(applyDate, DATE_STEP_APPLY_MS), [applyDate]);
  // Flushed, not cancelled: the click was made on the view being left, so it lands there rather than being lost.
  useEffect(
    () => () => {
      commitSoon.flush();
    },
    [commitSoon]
  );

  const onChange = useCallback(
    (next: Date | null, context?: PickerChangeHandlerContext<DateValidationError>) => {
      showPending(next);
      if (context?.shortcut) {
        // A rail chip leaves the popup open for the next click.
        commitSoon(next);
        return;
      }
      // Anything else (typing, popup open or not) takes over from a click that has not applied yet.
      // A day click is queued again by its own onAccept.
      commitSoon.cancel();
      if (popupOpenRef.current) {
        return;
      }
      // An impossible date drops the one an earlier keystroke queued, and stays shown so it can be fixed.
      if (next !== null && !isDateInputValid(next)) {
        commitTyped.cancel();
        return;
      }
      commitTyped(next);
    },
    [commitSoon, commitTyped, showPending]
  );

  // A day click, a click away or Escape.
  const onAccept = useCallback(
    (next: Date | null) => {
      commitTyped.cancel();
      commitSoon(next);
    },
    [commitSoon, commitTyped]
  );

  const stepTo = useCallback(
    (next: Date) => {
      commitTyped.cancel();
      showPending(next);
      commitSoon(next);
    },
    [commitSoon, commitTyped, showPending]
  );

  const onOpen = useCallback(() => {
    commitTyped.cancel();
    popupOpenRef.current = true;
  }, [commitTyped]);

  const onClose = useCallback(() => {
    popupOpenRef.current = false;
    // Closing by the calendar icon reports no value of its own, so every exit applies what is
    // shown. Applying twice is free: the second pass is the same day and writes nothing.
    if (pendingRef.current !== undefined) {
      commitSoon(pendingRef.current);
    }
  }, [commitSoon]);

  if (!selectedView || !viewsWithDatePicker.has(selectedView.viewType)) {
    return null;
  }

  return (
    <Stack display='grid' gridTemplateColumns={'auto 1fr'} alignItems={'center'} width={'100%'}>
      <StyledLabel role='label'>As Of Date</StyledLabel>
      <Stack direction='row' alignItems='center' gap='0.25rem'>
        <Tooltip title='Previous quarter'>
          {/* A disabled button fires no events, so the tooltip listens on the span. */}
          <span>
            <IconButton
              size='small'
              aria-label='previous quarter'
              disabled={isLoading}
              onClick={() => stepTo(previousQuarterEnd(stepFrom))}
            >
              <ChevronLeftIcon color={isLoading ? 'disabled' : 'secondary'} />
            </IconButton>
          </span>
        </Tooltip>
        <div title='As of date'>
          <CalendarCurrency
            value={shown}
            onChange={onChange}
            onAccept={onAccept}
            onOpen={onOpen}
            onClose={onClose}
            shortcutItems={shortcutItems}
            shortcutChangeImportance='set'
            disabled={isLoading}
            sx={{ maxWidth: '12rem' }}
            debounceTime={TYPED_DATE_APPLY_MS}
            selectedCurrency={selectedCurrency}
          />
        </div>
        <Tooltip title='Next quarter'>
          <span>
            <IconButton
              size='small'
              aria-label='next quarter'
              disabled={isLoading}
              onClick={() => stepTo(nextQuarterEnd(stepFrom))}
            >
              <ChevronRightIcon color={isLoading ? 'disabled' : 'secondary'} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
